import {useEffect, useRef, useCallback} from 'react';

/**
 * Hook to trigger a callback when the user scrolls near a sentinel element (infinite scroll).
 * Uses IntersectionObserver; attaches when the sentinel mounts so it works even if the
 * sentinel is rendered after initial load (e.g. when !isLoading).
 *
 * @param {Function} onLoadMore - Called when the sentinel becomes visible (e.g. load next page).
 * @param {Object} options
 * @param {boolean} options.enabled - Whether to observe and call onLoadMore (e.g. when hasMore && !isLoading).
 * @param {string} options.rootMargin - Root margin for the observer (default: '200px' to trigger a bit before bottom).
 * @param {number} options.threshold - Intersection threshold 0–1 (default: 0).
 * @param {Element|null} options.root - Optional scroll container element for IntersectionObserver (default: null = viewport).
 * @returns {Function} Callback ref to attach to the sentinel element at the bottom of the list.
 */
const COOLDOWN_MS = 500;

export function useInfiniteScroll(onLoadMore, options = {}) {
  const {enabled = true, rootMargin = '200px', threshold = 0, root = null} = options;
  const loadingRef = useRef(false);
  const lastLoadTimeRef = useRef(0);
  const observerRef = useRef(null);
  const sentinelElRef = useRef(null);
  const optionsRef = useRef({root, rootMargin, threshold});
  optionsRef.current = {root, rootMargin, threshold};

  const observe = useCallback(
    el => {
      if (!el || typeof onLoadMore !== 'function') return;
      const {root: r, rootMargin: rm, threshold: th} = optionsRef.current;
      const observer = new IntersectionObserver(
        entries => {
          const [entry] = entries;
          if (!entry?.isIntersecting) return;
          if (loadingRef.current) return;
          if (Date.now() - lastLoadTimeRef.current < COOLDOWN_MS) return;
          loadingRef.current = true;
          observerRef.current?.disconnect();
          observerRef.current = null;
          Promise.resolve()
            .then(() => onLoadMore())
            .finally(() => {
              lastLoadTimeRef.current = Date.now();
              loadingRef.current = false;
              if (sentinelElRef.current) {
                observe(sentinelElRef.current);
              }
            });
        },
        {root: r || null, rootMargin: rm, threshold: th},
      );
      observer.observe(el);
      observerRef.current = observer;
    },
    [onLoadMore],
  );

  const disconnect = useCallback(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
  }, []);

  const callbackRef = useCallback(
    el => {
      disconnect();
      sentinelElRef.current = el;
      if (!el) return;
      if (!enabled) return;
      observe(el);
    },
    [enabled, observe, disconnect],
  );

  useEffect(() => {
    return disconnect;
  }, [disconnect]);

  return callbackRef;
}
